/**
 * OC-fire clickability — the co-located OC-fire ↔ hotspot reconciliation seam
 * (Coordination Re-Architecture council §A "OC-fire unclickable — named must-fix").
 *
 * The §A ruling prescribes TWO halves — "try fires first in the pick cycle AND suppress
 * the redundant hotspot … one clickable flame, not a stack":
 *   1. SUPPRESS the redundant co-located hotspot dot — the OC-FUL visual de-clutter
 *      (`isHotspotCoLocatedWithOcFul`, Phase 5a / #409).
 *   2. TRY FIRES FIRST in the pick cycle (`pickFireUnderCoLocatedHotspot`, #439) — the
 *      robust clickability backstop that re-points the pick to the fire whenever a
 *      hotspot-family marker (high-risk / fusion-candidate / raw) is topmost over a
 *      co-located fire, for ANY fire stage and regardless of whether the dot was
 *      suppressed (covers the first-paint race + every interceptor layer in ONE place).
 * Phase 5a shipped only half 1; #439 adds the prescribed half 2. This file hosts both.
 *
 * When a CWFIS-confirmed OC-FUL fire (`ActiveFiresLayer`) and an AI high-risk hotspot
 * dot (`HighRiskHotspotsLayer`) sit at the same spot, the hotspot renders OVER the
 * fire, so the topmost-pick click grabs the hotspot and the fire never opens
 * (`MapView` LEFT_CLICK: `firesLayer.pick` only ever sees the topmost entity). The §A
 * ruling is "one clickable fire, not a stack": SUPPRESS the redundant co-located
 * hotspot dot, leaving the OC-FUL fire as the single clickable marker — with it
 * gone-to-the-top, `scene.pick` returns it and the existing fire branch opens the
 * fire detail.
 *
 * This is the spatial half: a pure predicate answering "is this hotspot genuinely
 * co-located with (stacked on) an OC-FUL fire?" Two precision guards (§G) live in the
 * inputs, not here: the caller passes ONLY OC-FUL fires (a non-OC-FUL nearby fire —
 * BH/UC/OUT, OC-Monitored — never suppresses, even though Phase 5c made all stages
 * render the same fire glyph: the glyph swap changed how fires LOOK, never which
 * fires SUPPRESS), and the radius is TIGHT (genuine overlap, NOT the loose 20 km
 * `active_fire_nearby` fusion link). The hotspot's underlying data / incident /
 * re-escalation (`realarm.ts`) are UNTOUCHED — only the redundant visual dot is hidden.
 */
import { haversineMeters } from '../cameraCluster';

/**
 * A currently-rendered OC-FUL fire: its id + map position (the fire's `centroid` —
 * where `ActiveFiresLayer` anchors the glyph billboard). `id` is carried for
 * diagnostics / a future semantic corroboration (the fusion's `active_fire_nearby_id`);
 * the predicate itself reads only the position.
 */
export interface OcFulFire {
  id: string;
  lon: number;
  lat: number;
}

/**
 * Tight co-location radius (metres). A hotspot is "stacked on" an OC-FUL fire only
 * when one sits within this distance — i.e. the two markers visually overlap at the
 * spot. Deliberately FAR below the 20 km `active_fire_nearby` fusion radius (which
 * corroborates "same incident region", NOT "same pixel"): the demo's Elliot Lake fire
 * + fused hotspot sit ~135 m apart, and a CWFIS perimeter centroid vs a VIIRS/MODIS
 * detection cluster for the SAME fire jitter sub-kilometre, so 1 km robustly catches
 * the genuine stack while never reaching a distinct, separately-clickable incident
 * (the §G over-suppression guard).
 */
export const COLOCATION_DISTANCE_M = 1_000;

/**
 * True when an OC-FUL fire is co-located with (stacked on) the hotspot at [lon, lat]
 * — one within `maxMeters`. Pure + exported for unit tests; the caller supplies only
 * OC-FUL fires, so any hit means the OC-FUL fire is the authoritative marker at the
 * spot and the redundant hotspot dot is suppressed.
 */
export function isHotspotCoLocatedWithOcFul(
  hotspotLon: number,
  hotspotLat: number,
  ocFulFires: readonly OcFulFire[],
  maxMeters: number = COLOCATION_DISTANCE_M,
): boolean {
  for (const fire of ocFulFires) {
    if (haversineMeters(hotspotLon, hotspotLat, fire.lon, fire.lat) <= maxMeters) {
      return true;
    }
  }
  return false;
}

/**
 * Injected lookups for {@link pickFireUnderCoLocatedHotspot}. Kept as callbacks so this
 * module stays free of Cesium / layer-handle imports and the override is unit-lockable
 * with plain objects (the MapView wiring supplies the real `*.pick()` walks).
 */
export interface FirePickPrecedenceDeps {
  /**
   * `[lon, lat]` when `entity` is a redundant fire-corroboration marker — a high-risk
   * hotspot, a fusion candidate, or a raw hotspot detection — else `null`. These are the
   * §A "the hotspot dot renders over the OC flame" interceptors; a camera / community /
   * aircraft / sector entity returns `null`, so the override never touches their clicks.
   */
  hotspotPositionOf: (entity: unknown) => readonly [number, number] | null;
  /** `[lon, lat]` of the fire's incident centroid when `entity` is a fire marker, else `null`. */
  fireCentroidOf: (entity: unknown) => readonly [number, number] | null;
  /**
   * Co-location radius (metres). Defaults to {@link COLOCATION_DISTANCE_M} — the SAME tight
   * 1 km the suppression half uses, so "stacked on the incident point" means one thing in
   * both halves (never the loose 20 km `active_fire_nearby` fusion link).
   */
  maxMeters?: number;
}

/**
 * "Try fires first in the pick cycle" — the SECOND half of the council §A "OC-fire
 * unclickable" ruling (Phase 5a / #409 shipped only the suppression half, so a fire that
 * is NOT OC-FUL, or is stacked under a fusion-candidate / raw-hotspot dot the suppression
 * never touches, stayed dead — the founder's "most OC-fire icons still unclickable, and
 * it's inconsistent").
 *
 * Given the topmost pick and the full top-to-bottom `drillPick` stack at a click, return
 * the pick result of the FIRE that should win when the topmost entity is a redundant
 * fire-corroboration marker (high-risk hotspot / fusion candidate / raw hotspot) stacked
 * WITHIN `maxMeters` of a fire's incident centroid — so the click opens the FireBody, not
 * the dot. Returns `null` when no override applies (topmost isn't a corroboration marker,
 * or no co-located fire sits in the stack) → the caller keeps its own topmost pick.
 *
 * Why this scoping is regression-safe:
 *  - It fires ONLY when the topmost pick is a hotspot-family marker, so it can never steal
 *    a click meant for a camera / community / aircraft / sector inside a fire's perimeter.
 *  - The tight centroid radius excludes a hotspot that merely sits inside a DISTANT fire's
 *    large perimeter polygon (centroid > `maxMeters`) — only a genuine on-the-marker stack
 *    is collapsed onto the fire.
 *  - It reads the already-computed `drilled` stack (no extra Cesium pick) and runs AFTER
 *    the #415 incident-cluster early-return and the #348a coverage-fill demotion, so it
 *    composes with both rather than replacing them.
 * Robust to EVERY interceptor layer and EVERY fire stage in ONE place, and a backstop when
 * the visual suppression hasn't yet hidden the dot (first-paint race). Pure + injected deps
 * so it's free of Cesium and unit-locked.
 */
export function pickFireUnderCoLocatedHotspot<P extends { id?: unknown }>(
  topmost: P | null | undefined,
  drilled: readonly P[],
  deps: FirePickPrecedenceDeps,
): P | null {
  if (!topmost) return null;
  const hotspotPos = deps.hotspotPositionOf(topmost.id);
  if (!hotspotPos) return null; // topmost is not a redundant hotspot-family marker → no override
  const maxM = deps.maxMeters ?? COLOCATION_DISTANCE_M;
  for (const hit of drilled) {
    const centroid = deps.fireCentroidOf(hit?.id);
    if (
      centroid &&
      haversineMeters(hotspotPos[0], hotspotPos[1], centroid[0], centroid[1]) <= maxM
    ) {
      return hit; // first (topmost) co-located fire in the stack wins the pick
    }
  }
  return null;
}
